import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Sprout,
  Users,
  Trash2,
  Mail,
  Download,
  CheckCircle2,
  Clock,
  RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { adminApi, auth, API } from "@/lib/adminApi";
import { ADMIN } from "@/constants/testIds/landing";
import AdminContent from "@/pages/admin/AdminContent";

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white rounded-2xl border border-[#EAEAEA] p-5 flex items-center gap-4"
    >
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-[#E8EFE1] text-[#6B8E23]">
        <Icon size={20} />
      </span>
      <div>
        <p className="text-xs uppercase tracking-wider text-[#888]">{label}</p>
        <p className="font-[Outfit] text-2xl font-semibold">{value}</p>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function downloadCsv(path, filename) {
  const token = auth.getToken();
  fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(async (res) => {
      if (!res.ok) throw new Error("Download fehlgeschlagen");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch((e) => toast.error(e.message || "Fehler beim Export"));
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [signups, setSignups] = useState([]);
  const [deletions, setDeletions] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [a, b] = await Promise.all([
        adminApi.get("/admin/tester-signups"),
        adminApi.get("/admin/deletion-requests"),
      ]);
      setSignups(a.data);
      setDeletions(b.data);
    } catch (e) {
      toast.error("Konnte Daten nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const stats = useMemo(
    () => ({
      total: signups.length,
      pending: deletions.filter((d) => d.status === "pending").length,
      emailsSent: signups.filter((s) => s.email_sent).length,
    }),
    [signups, deletions]
  );

  const logout = () => {
    auth.clear();
    toast.success("Abgemeldet.");
    navigate("/admin/login", { replace: true });
  };

  const resendEmail = async (id) => {
    try {
      const { data } = await adminApi.post(`/admin/tester-signups/${id}/resend-email`);
      if (data?.sent) {
        toast.success("Bestätigungs-Mail erneut gesendet.");
      } else {
        toast.error(data?.detail || "E-Mail-Versand fehlgeschlagen.");
      }
      refresh();
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "E-Mail-Versand fehlgeschlagen.";
      toast.error(msg);
    }
  };

  const deleteSignup = async (id, email) => {
    if (!window.confirm(`Eintrag ${email} wirklich löschen?`)) return;
    try {
      await adminApi.delete(`/admin/tester-signups/${id}`);
      toast.success("Eintrag gelöscht.");
      refresh();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const setDeletionStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/deletion-requests/${id}`, { status });
      toast.success("Status aktualisiert.");
      refresh();
    } catch (e) {
      toast.error("Update fehlgeschlagen.");
    }
  };

  return (
    <div
      data-testid={ADMIN.dashboard}
      className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]"
    >
      <header className="bg-white border-b border-[#EAEAEA] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-[#6B8E23] text-white">
              <Sprout size={20} strokeWidth={2.2} />
            </span>
            <span className="font-[Outfit] font-bold text-xl">Waskowi Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-[#444] hover:bg-[#FAFAFA] border border-transparent hover:border-[#EAEAEA] transition-all"
            >
              <RotateCw size={16} /> Aktualisieren
            </button>
            <button
              onClick={logout}
              data-testid={ADMIN.logoutButton}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-[#1A1A1A] text-white hover:bg-[#B42318] transition-all"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight">
            Dashboard
          </h1>
          <p className="text-[#666] mt-1">
            Verwalte Testanmeldungen und Löschanfragen.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Tester gesamt"
            value={stats.total}
            testId={ADMIN.statBoxSignups}
          />
          <StatCard
            icon={Mail}
            label="Mails versendet"
            value={stats.emailsSent}
            testId={ADMIN.statBoxEmailsSent}
          />
          <StatCard
            icon={Clock}
            label="Offene Löschanfragen"
            value={stats.pending}
            testId={ADMIN.statBoxDeletionsPending}
          />
        </div>

        <Tabs defaultValue="signups" className="w-full">
          <TabsList className="bg-white border border-[#EAEAEA] rounded-full p-1">
            <TabsTrigger
              value="signups"
              data-testid={ADMIN.tabSignups}
              className="rounded-full data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white px-5"
            >
              Tester ({signups.length})
            </TabsTrigger>
            <TabsTrigger
              value="deletions"
              data-testid={ADMIN.tabDeletions}
              className="rounded-full data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white px-5"
            >
              Löschanfragen ({deletions.length})
            </TabsTrigger>
            <TabsTrigger
              value="content"
              data-testid={ADMIN.tabContent}
              className="rounded-full data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white px-5"
            >
              Inhalte
            </TabsTrigger>
          </TabsList>

          {/* SIGNUPS */}
          <TabsContent value="signups" className="mt-6">
            <div className="flex justify-end mb-3">
              <button
                onClick={() =>
                  downloadCsv("/admin/export/tester-signups.csv", "meieiko-tester.csv")
                }
                data-testid={ADMIN.signupsExportCsv}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-[#6B8E23] text-white hover:bg-[#55711C] transition-all"
              >
                <Download size={16} /> CSV exportieren
              </button>
            </div>
            <div className="bg-white border border-[#EAEAEA] rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table data-testid={ADMIN.signupsTable} className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#888] bg-[#FAFAFA] uppercase text-xs">
                      <th className="px-5 py-3">E-Mail</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Mail gesendet</th>
                      <th className="px-5 py-3">Angemeldet</th>
                      <th className="px-5 py-3 text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-[#888]">
                          Lädt…
                        </td>
                      </tr>
                    ) : signups.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-[#888]">
                          Noch keine Anmeldungen.
                        </td>
                      </tr>
                    ) : (
                      signups.map((s) => (
                        <tr
                          key={s.id}
                          data-testid={`${ADMIN.signupRow}-${s.id}`}
                          className="border-t border-[#EFEFEF]"
                        >
                          <td className="px-5 py-3 font-medium">{s.email}</td>
                          <td className="px-5 py-3 text-[#666]">{s.name || "—"}</td>
                          <td className="px-5 py-3">
                            {s.email_sent ? (
                              <span className="inline-flex items-center gap-1 text-[#3A7D1E] text-xs font-medium">
                                <CheckCircle2 size={14} /> ja
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[#999] text-xs">
                                <Clock size={14} /> nein
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-[#666] text-xs">
                            {formatDate(s.created_at)}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => resendEmail(s.id)}
                                data-testid={`${ADMIN.signupResendEmail}-${s.id}`}
                                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium border border-[#E5E5E5] hover:border-[#6B8E23] hover:text-[#6B8E23] transition-colors"
                              >
                                <Mail size={14} /> Mail
                              </button>
                              <button
                                onClick={() => deleteSignup(s.id, s.email)}
                                data-testid={`${ADMIN.signupDelete}-${s.id}`}
                                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium border border-[#E5E5E5] hover:border-[#B42318] hover:text-[#B42318] transition-colors"
                              >
                                <Trash2 size={14} /> Löschen
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* DELETIONS */}
          <TabsContent value="deletions" className="mt-6">
            <div className="flex justify-end mb-3">
              <button
                onClick={() =>
                  downloadCsv(
                    "/admin/export/deletion-requests.csv",
                    "meieiko-deletions.csv"
                  )
                }
                data-testid={ADMIN.deletionsExportCsv}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-[#6B8E23] text-white hover:bg-[#55711C] transition-all"
              >
                <Download size={16} /> CSV exportieren
              </button>
            </div>
            <div className="bg-white border border-[#EAEAEA] rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table data-testid={ADMIN.deletionsTable} className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#888] bg-[#FAFAFA] uppercase text-xs">
                      <th className="px-5 py-3">E-Mail</th>
                      <th className="px-5 py-3">Grund</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Eingegangen</th>
                      <th className="px-5 py-3 text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-[#888]">
                          Lädt…
                        </td>
                      </tr>
                    ) : deletions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-[#888]">
                          Noch keine Löschanfragen.
                        </td>
                      </tr>
                    ) : (
                      deletions.map((d) => (
                        <tr
                          key={d.id}
                          data-testid={`${ADMIN.deletionRow}-${d.id}`}
                          className="border-t border-[#EFEFEF]"
                        >
                          <td className="px-5 py-3 font-medium">{d.email}</td>
                          <td className="px-5 py-3 text-[#666] max-w-[280px] truncate">
                            {d.reason || "—"}
                          </td>
                          <td className="px-5 py-3">
                            {d.status === "done" ? (
                              <span className="inline-flex items-center gap-1 text-[#3A7D1E] bg-[#E8EFE1] rounded-full px-3 py-1 text-xs font-medium">
                                <CheckCircle2 size={14} /> erledigt
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[#9A6A00] bg-[#FFF4D6] rounded-full px-3 py-1 text-xs font-medium">
                                <Clock size={14} /> offen
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-[#666] text-xs">
                            {formatDate(d.created_at)}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {d.status === "pending" ? (
                                <button
                                  onClick={() => setDeletionStatus(d.id, "done")}
                                  data-testid={`${ADMIN.deletionMarkDone}-${d.id}`}
                                  className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium bg-[#6B8E23] text-white hover:bg-[#55711C] transition-colors"
                                >
                                  <CheckCircle2 size={14} /> Erledigt
                                </button>
                              ) : (
                                <button
                                  onClick={() => setDeletionStatus(d.id, "pending")}
                                  data-testid={`${ADMIN.deletionMarkPending}-${d.id}`}
                                  className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium border border-[#E5E5E5] hover:border-[#6B8E23] hover:text-[#6B8E23] transition-colors"
                                >
                                  <Clock size={14} /> Wieder offen
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* CONTENT */}
          <TabsContent value="content" className="mt-6">
            <AdminContent />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
